/**
 * Nass Programs Grid — quick add-to-cart for the programs catalogue cards.
 * Same cart pattern as nass-offer-cards.js: optimistic CartLinesUpdateEvent
 * with a deferred promise, /cart/add.js with bundled section rendering, then
 * resolve with the ajax cart so the header badge and cart drawer stay in sync
 * — finally open the cart drawer (OCU-aware). The whole card is a link to the
 * program land; this button is the quick-buy path only.
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';

function getCartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset?.sectionId).filter(Boolean);
}

function initAddButton(button) {
  let isProcessing = false;

  button.addEventListener('click', async (event) => {
    // The card itself is a full-cover link; stop the click from following it.
    event.preventDefault();
    event.stopPropagation();

    const variantId = Number(button.dataset.variantId);
    if (!variantId || isProcessing) return;
    isProcessing = true;
    button.classList.add('is-busy');

    const sectionIds = getCartSectionIds();
    const items = [{ id: variantId, quantity: 1 }];
    const body = { items, sections: sectionIds.join(','), sections_url: window.location.pathname };

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`cart/add failed (${res.status}) ${text.slice(0, 140)}`.trim());
      }
      const data = await res.json();
      if (data.status) throw new Error(data.message || 'cart/add failed');

      if (data.sections && sectionIds.length > 0) {
        for (const sectionId of sectionIds) {
          const html = data.sections[sectionId];
          if (html) await morphSection(sectionId, html, { mode: 'hydration' });
        }
      }

      const ajaxCart = await (await fetch('/cart.js')).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { items: ajaxCart.items, source: 'nass-programs-grid', didError: false },
      });

      // Open the cart drawer (OCU opens its own slide cart when active).
      const themeDrawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
      if (window.NassOcu) {
        window.NassOcu.openCartOrTheme(themeDrawer);
      } else if (themeDrawer && !themeDrawer.isOpen && typeof themeDrawer.open === 'function') {
        themeDrawer.open();
      }
    } catch (err) {
      console.error(err);
      deferred.reject(err);
    } finally {
      isProcessing = false;
      button.classList.remove('is-busy');
    }
  });
}

document.querySelectorAll('[data-nass-pg-add]').forEach(initAddButton);
